import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  Color,
  HorizontalLine,
  LoadingGroup,
  LoadingSkeleton,
  Message,
  Panel,
  Table,
  Tag,
  TagType,
  useSimpleTableDataSource,
} from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Paragraph, Semibold, Subheading, Subtitle } from "capitalrx-components-2.0/lib/typography";
import { formatDate, formatDollars } from "capitalrx-utilities";
import { DateTime } from "luxon";

import { useListMedicarePrescriptionPaymentPlanDueInvoicesQuery, useListMedicarePrescriptionPaymentPlanPaymentQuery } from "@/App/api/medicarePrescriptionPaymentPlan";
import { UniformRowColorTable } from "@/App/components";
import { usePlanYear } from "@/App/pages";

import { MakeAPaymentForm } from "./MakeAPaymentForm";

const TRANSLATION_PATH = "MEDICARE_PRESCRIPTION_PAYMENT_PLAN.paymentInformationTransactionsPanel";

const paymentStatusMapping: Record<string, { label: string; tagStyle: string; textStyle: Color.RgbColor }> = {
  unpaid: {
    label: "paymentDue",
    tagStyle: "info-high-contrast",
    textStyle: Color.DarkGrey[500],
  },
  partially_paid: {
    label: "partiallyPaid",
    tagStyle: "warning-high-contrast",
    textStyle: Color.DarkGrey[500],
  },
  late: {
    label: "pastDue",
    tagStyle: "error-high-contrast",
    textStyle: Color.Red[600],
  },
};

export const TransactionsPanel: FC = () => {
  const { t: translate } = useTranslation();
  const planYear = usePlanYear();

  const { data: dueInvoices, isLoading: isLoadingDueInvoices } = useListMedicarePrescriptionPaymentPlanDueInvoicesQuery(planYear);
  const { data: transactions, isLoading: isLoadingTransactions } = useListMedicarePrescriptionPaymentPlanPaymentQuery({ plan_year: planYear, page_number: 1 });
  const isLoading = isLoadingDueInvoices || isLoadingTransactions;

  const { isCompressed } = useResponsive({
    isCompressed: {
      mobile: true,
      desktop: false,
    },
  });

  const currentAmountDue = useMemo(() => {
    return (dueInvoices?.results || [])
      .map((invoice) => invoice.amount_due)
      .reduce((prev: number, curr: number) => {
        return curr + prev;
      }, 0);
  }, [dueInvoices]);

  const StackOrShelf = isCompressed ? Stack : Shelf;

  const paymentDueDate = useMemo(() => {
    const latestInvoiceDueDate = (dueInvoices?.results || [])
      .map((invoice) => DateTime.fromISO(invoice.due_date))
      .sort((a, b) => {
        return b.diff(a).milliseconds;
      })[0];

    if (latestInvoiceDueDate) {
      return latestInvoiceDueDate.toUTC().toFormat("MM/dd/yyyy");
    } else {
      return "--";
    }
  }, [dueInvoices]);

  const anyProcessingPayments = transactions?.results.some((transaction) => transaction.status === "processing");

  const paymentSummaryRows = useMemo(() => {
    return (dueInvoices?.results || [])
      .filter((invoice) => invoice.reconciliation_status !== "paid_in_full")
      .map((invoice) => {
        const isPaymentLate =
          invoice.reconciliation_status === "unpaid"
          && DateTime.now().toISODate() > DateTime.fromISO(invoice.due_date, { setZone: true }).toISODate();
        const mapping = paymentStatusMapping[isPaymentLate ? "late" : invoice.reconciliation_status];
        /* let infoText;
           if (invoice.reconciliation_status === "partially_paid") {
             infoText = "You are still responsible for this payment.";
           } else if (invoice.reconciliation_status === "delinquent" && isParticipating) {
             infoText = `If you do not pay this amount by ${formatDate(
               invoice.grace_period_end_date,
               "MM/DD/YYYY",
             )}, you will be opted out of the program.`;
           } else if (invoice.reconciliation_status === "delinquent" && !isParticipating) {
             infoText = "You are still responsible for this payment.";
           } */
        return {
          id: invoice.id,
          data: {
            due_date: invoice.due_date,
            amount_due: invoice.amount_due,
            status: {
              text: translate(`${TRANSLATION_PATH}.${mapping.label}`),
              status_tag: mapping.tagStyle,
            },
            // info: <TextColor appearance={mapping.textStyle}>{infoText}</TextColor>,
          },
        };
      });
  }, [dueInvoices]);

  const paymentSummaryDataSource = useSimpleTableDataSource(paymentSummaryRows);

  return (
    <Panel
      appearance="primary"
      style={{ backgroundColor: "white", border: `solid 1px #CFD9E1`, boxShadow: "0 0 0.25rem rgba(0, 0, 0, 0.08)" }}
    >
      <Stack spacing="large">
        <LoadingGroup loading={isLoading}>
          <LoadingGroup.Placeholder
            fallback={
              <StackOrShelf spacing="large">
                <Stack spacing="mini">
                  <LoadingSkeleton style={{ width: "100px", height: "32px" }} />
                  <LoadingSkeleton style={{ width: "100px", height: "16px" }} />
                </Stack>
                <Stack spacing="mini">
                  <LoadingSkeleton style={{ width: "100px", height: "32px" }} />
                  <LoadingSkeleton style={{ width: "100px", height: "16px" }} />
                </Stack>
                <Shelf.Spacer />
              </StackOrShelf>
            }
          >
            <StackOrShelf spacing="large">
              <Stack spacing="mini">
                <Subtitle>{formatDollars(currentAmountDue)}</Subtitle>
                <Body>{translate(`${TRANSLATION_PATH}.totalPaymentDue`)}</Body>
              </Stack>

              {currentAmountDue > 0 && (
                <Stack spacing="mini">
                  <Subtitle>{paymentDueDate}</Subtitle>
                  <Body>{translate(`${TRANSLATION_PATH}.paymentDueDate`)}</Body>
                </Stack>
              )}
              <Shelf.Spacer />
            </StackOrShelf>
          </LoadingGroup.Placeholder>
        </LoadingGroup>

        {currentAmountDue > 0 && (
          <LoadingGroup loading={isLoading}>
            <LoadingGroup.Placeholder
              fallback={
                <Stack spacing="mini">
                  <LoadingSkeleton style={{ width: "75px", height: "16px" }} />
                  <Shelf spacing="mini">
                    <LoadingSkeleton style={{ width: "100px", height: "32px" }} />
                    <LoadingSkeleton style={{ width: "100px", height: "32px" }} />
                    <LoadingSkeleton style={{ width: "100px", height: "32px" }} />
                    <Shelf.Spacer />
                  </Shelf>
                  <Shelf spacing="mini">
                    <LoadingSkeleton style={{ width: "100px", height: "32px" }} />
                    <LoadingSkeleton style={{ width: "100px", height: "32px" }} />
                    <LoadingSkeleton style={{ width: "100px", height: "32px" }} />
                    <Shelf.Spacer />
                  </Shelf>
                </Stack>
              }
            >
              <Stack spacing="medium">
                <Shelf>
                  <Subheading>{translate(`${TRANSLATION_PATH}.paymentSummary`)}</Subheading>
                  <Shelf.Spacer />
                </Shelf>
                {!isCompressed && (
                  <Shelf>
                    <UniformRowColorTable dataSource={paymentSummaryDataSource}>
                      <Table.Date format="MM/dd/yyyy" title={translate(`${TRANSLATION_PATH}.dueDate`)} name="due_date" />
                      <Table.Custom title={translate(`${TRANSLATION_PATH}.amountDue`)} name="amount_due">
                        {(value: string) => {
                          return <Paragraph>{formatDollars(value)}</Paragraph>;
                        }}
                      </Table.Custom>
                      <Table.Custom title={translate(`${TRANSLATION_PATH}.status`)} name="status" align="center">
                        {(value: { status_tag: TagType; text: string }) => {
                          return (
                            <Shelf>
                              <Shelf.Spacer />
                              <Tag type={value.status_tag}>{value.text}</Tag>
                              <Shelf.Spacer />
                            </Shelf>
                          );
                        }}
                      </Table.Custom>
                    </UniformRowColorTable>
                    <Shelf.Spacer />
                  </Shelf>
                )}
                {isCompressed
                  && paymentSummaryRows.map((payment) => {
                    return (
                      <Stack key={payment.id}>
                        <Stack spacing="mini">
                          <Body>
                            <Semibold>{translate(`${TRANSLATION_PATH}.dueDate`)}</Semibold>
                          </Body>
                          <Body>{formatDate(payment.data.due_date, "MM/DD/YYYY")}</Body>
                        </Stack>
                        <Stack spacing="mini">
                          <Body>
                            <Semibold>{translate(`${TRANSLATION_PATH}.amountDue`)}</Semibold>
                          </Body>
                          <Body>{formatDollars(payment.data.amount_due)}</Body>
                        </Stack>
                        <Stack spacing="mini">
                          <Body>
                            <Semibold>{translate(`${TRANSLATION_PATH}.status`)}</Semibold>
                          </Body>
                          <Shelf>
                            <Tag type={payment.data.status.status_tag as TagType}>{payment.data.status.text}</Tag>
                            <Shelf.Spacer />
                          </Shelf>
                        </Stack>
                        {paymentSummaryRows.length > 1 && <HorizontalLine />}
                      </Stack>
                    );
                  })}
              </Stack>
            </LoadingGroup.Placeholder>
          </LoadingGroup>
        )}
        <Message visible={anyProcessingPayments}>{translate(`${TRANSLATION_PATH}.paymentProcessing`)}</Message>
        <MakeAPaymentForm />
      </Stack>
    </Panel>
  );
};
